import React, { useEffect, useState, useCallback } from 'react';
import {
  collection, onSnapshot, doc, updateDoc, deleteDoc,
  serverTimestamp, query, orderBy, getDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, XCircle, Clock, Trash2, Store, Phone, Users, Eye,
  TrendingUp, AlertTriangle, ChevronDown, ChevronUp, X, Check,
  Shield, BarChart2, ShoppingBag, Search, Bell, Crown, Zap, Star,
  Gift, User, Lock, Unlock, RefreshCw, Send, Package, Calendar,
  ToggleLeft, ToggleRight,
} from 'lucide-react';

/* ─────────────────────────────────────────────────────────────────
   HOOK — statistiques visiteurs
───────────────────────────────────────────────────────────────── */
const useVisitorStats = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const ref = doc(db, 'stats', 'visitors');
    const unsub = onSnapshot(ref, snap => {
      if (snap.exists()) {
        const data = snap.data();
        const today = new Date().toISOString().slice(0, 10);
        const fromMap  = data.days?.[today] ?? 0;
        const fromRoot = data[`days.${today}`] ?? 0;
        setStats({ total: data.total || 0, today: Math.max(fromMap, fromRoot) });
      } else {
        setStats({ total: 0, today: 0 });
      }
    });
    return () => unsub();
  }, []);
  return {
    total:      stats?.total ?? '—',
    today:      stats?.today ?? '—',
  };
};

/* ─────────────────────────────────────────────────────────────────
   PACK CONFIG
───────────────────────────────────────────────────────────────── */
const PACK_CFG = {
  free:    { label: 'Gratuit',  color: '#94a3b8', icon: Gift   },
  starter: { label: 'Starter',  color: '#60a5fa', icon: Zap    },
  premium: { label: 'Premium',  color: '#a78bfa', icon: Star   },
  pro:     { label: 'Pro',      color: '#f59e0b', icon: Crown  },
};

/* ─────────────────────────────────────────────────────────────────
   SOUS-COMPOSANTS UTILITAIRES
───────────────────────────────────────────────────────────────── */
const StatCard = ({ icon: Icon, iconColor, iconBg, label, value, sub }) => (
  <div className="admin-stat-card">
    <div style={{ width: 38, height: 38, borderRadius: 'var(--r-lg)', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={18} color={iconColor} strokeWidth={2}/>
    </div>
    <div>
      <div style={{ fontWeight: 800, fontSize: '1.35rem', color: 'var(--text)', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '0.2rem' }}>{label}</div>
      {sub && <div style={{ fontSize: '0.68rem', color: 'var(--text-3)', marginTop: '0.1rem' }}>{sub}</div>}
    </div>
  </div>
);

const ApprovalPill = ({ approval }) => {
  const cfg = {
    approved: { bg: 'rgba(0,201,167,.15)',  color: '#00c9a7', label: 'Approuvé' },
    rejected: { bg: 'rgba(240,86,74,.15)',  color: '#f0564a', label: 'Rejeté'   },
    pending:  { bg: 'rgba(245,166,35,.15)', color: '#f5a623', label: 'En attente'},
  }[approval] || { bg: 'rgba(245,166,35,.15)', color: '#f5a623', label: 'En attente' };
  return (
    <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.55rem', borderRadius: '999px', background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
};

const PackBadge = ({ packId }) => {
  const cfg = PACK_CFG[packId] || PACK_CFG.free;
  const Icon = cfg.icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.65rem', fontWeight: 700, padding: '0.2rem 0.55rem', borderRadius: '999px', background: `${cfg.color}20`, color: cfg.color }}>
      <Icon size={10} strokeWidth={2}/> {cfg.label}
    </span>
  );
};

const ConfirmModal = ({ message, onConfirm, onCancel }) => (
  <div onClick={onCancel} style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
    <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg)', borderRadius: 'var(--r-xl)', padding: '1.5rem', maxWidth: 320, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text)', marginBottom: '1rem', lineHeight: 1.4 }}>{message}</div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '0.65rem', borderRadius: 'var(--r-lg)', border: '1px solid var(--outline)', background: 'var(--bg-elevated)', color: 'var(--text-2)', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Annuler</button>
        <button onClick={onConfirm} style={{ flex: 1, padding: '0.65rem', borderRadius: 'var(--r-lg)', border: 'none', background: 'linear-gradient(135deg,#f0564a,#dc2626)', color: '#fff', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Confirmer</button>
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   MODAL APPROBATION
───────────────────────────────────────────────────────────────── */
const ApprovalModal = ({ shop, onClose, onApprove, onReject }) => {
  const [reason, setReason]   = useState(shop.rejectionReason || '');
  const [loading, setLoading] = useState(false);
  const [action, setAction]   = useState(null);

  const handle = async (type) => {
    if (type === 'reject' && !reason.trim()) return;
    setAction(type); setLoading(true);
    try {
      if (type === 'approve') await onApprove(shop.id);
      else await onReject(shop.id, reason);
      onClose();
    } finally { setLoading(false); setAction(null); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg)', borderRadius: 'var(--r-xl) var(--r-xl) 0 0', width: '100%', maxWidth: 480, padding: '1.5rem', boxShadow: '0 -8px 40px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
          <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text)' }}>Décision — {shop.name}</div>
          <button onClick={onClose} style={{ background: 'var(--bg-elevated)', border: 'none', borderRadius: '50%', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-2)' }}><X size={14}/></button>
        </div>
        <button onClick={() => handle('approve')} disabled={loading} style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--r-lg)', border: 'none', background: 'linear-gradient(135deg,#00c9a7,#00b396)', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          {loading && action === 'approve' ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }}/> : <Check size={14}/>} Approuver
        </button>
        <div style={{ marginBottom: '0.5rem' }}>
          <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-2)', display: 'block', marginBottom: '0.4rem' }}>Raison du rejet *</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="Ex: Adresse incomplète, type de commerce non autorisé…" style={{ width: '100%', padding: '0.7rem', borderRadius: 'var(--r-lg)', border: '1.5px solid var(--outline)', background: 'var(--bg-elevated)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.85rem', resize: 'none', outline: 'none', boxSizing: 'border-box' }}/>
        </div>
        <button onClick={() => handle('reject')} disabled={loading || !reason.trim()} style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--r-lg)', border: 'none', background: reason.trim() ? 'linear-gradient(135deg,#f0564a,#dc2626)' : 'var(--outline)', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: reason.trim() ? 'pointer' : 'not-allowed', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
          {loading && action === 'reject' ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }}/> : <XCircle size={14}/>} Rejeter
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ONGLET 1 — MAGASINS
───────────────────────────────────────────────────────────────── */
const ShopsTab = ({ shops, onApprove, onReject, onDelete, onForceStatus, onUnlockStatus }) => {
  const [filter, setFilter]   = useState('pending');
  const [search, setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm]   = useState(null);

  const counts = {
    all:      shops.length,
    pending:  shops.filter(s => !s.approval || s.approval === 'pending').length,
    approved: shops.filter(s => s.approval === 'approved').length,
    rejected: shops.filter(s => s.approval === 'rejected').length,
  };

  const filtered = shops
    .filter(s => filter === 'all' ? true : filter === 'pending' ? (!s.approval || s.approval === 'pending') : s.approval === filter)
    .filter(s => !search || s.name?.toLowerCase().includes(search.toLowerCase()) || s.address?.toLowerCase().includes(search.toLowerCase()) || s.ownerName?.toLowerCase().includes(search.toLowerCase()));

  const TABS = [
    { key: 'pending',  label: 'En attente', count: counts.pending  },
    { key: 'approved', label: 'Approuvés',  count: counts.approved },
    { key: 'rejected', label: 'Rejetés',    count: counts.rejected },
    { key: 'all',      label: 'Tous',       count: counts.all      },
  ];

  const STATUS_LABELS = { open: 'Ouvert', closed: 'Fermé', uncertain: 'Incertain', be_back: 'De retour' };
  const STATUS_COLORS = { open: '#00c9a7', closed: '#f0564a', uncertain: '#f5a623', be_back: '#60a5fa' };

  return (
    <div>
      {/* Barre de recherche */}
      <div style={{ position: 'relative', margin: '0 1rem 0.75rem' }}>
        <Search size={15} style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}/>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par nom, adresse, propriétaire…"
          style={{ width: '100%', padding: '0.65rem 0.85rem 0.65rem 2.4rem', borderRadius: 'var(--r-lg)', border: '1.5px solid var(--outline)', background: 'var(--bg-elevated)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}/>
      </div>

      {/* Onglets filtre */}
      <div style={{ display: 'flex', gap: '0.35rem', padding: '0 1rem', marginBottom: '0.75rem', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setFilter(t.key)} style={{ padding: '0.35rem 0.75rem', borderRadius: '999px', border: 'none', background: filter === t.key ? 'var(--primary)' : 'var(--bg-elevated)', color: filter === t.key ? '#fff' : 'var(--text-2)', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.3rem', boxShadow: filter === t.key ? '0 2px 8px rgba(0,201,167,0.3)' : 'var(--neu-sm)' }}>
            {t.label} {t.count > 0 && <span style={{ background: filter === t.key ? 'rgba(255,255,255,0.25)' : 'var(--outline)', borderRadius: '999px', padding: '0.05rem 0.4rem', fontSize: '0.62rem' }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {/* Liste */}
      <div style={{ padding: '0 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {filtered.length === 0 && (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-3)', fontSize: '0.85rem' }}>
            Aucun magasin trouvé.
          </div>
        )}
        {filtered.map(shop => (
          <div key={shop.id} className="card" style={{ margin: 0, padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.8rem 1rem', cursor: 'pointer' }} onClick={() => setExpanded(expanded === shop.id ? null : shop.id)}>
              <div style={{ width: 36, height: 36, borderRadius: 'var(--r-md)', background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: 'var(--neu-sm)' }}>
                <Store size={16} color="var(--primary)" strokeWidth={1.8}/>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shop.name}</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shop.address}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                <ApprovalPill approval={shop.approval}/>
                {expanded === shop.id ? <ChevronUp size={14} color="var(--text-3)"/> : <ChevronDown size={14} color="var(--text-3)"/>}
              </div>
            </div>

            {expanded === shop.id && (
              <div style={{ borderTop: '1px solid var(--outline)', padding: '0.85rem 1rem' }}>
                {/* Infos */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.85rem' }}>
                  {shop.ownerName && <div style={{ display: 'flex', gap: '0.4rem', fontSize: '0.78rem', color: 'var(--text-2)' }}><User size={12} style={{ flexShrink: 0, marginTop: 2 }}/> {shop.ownerName}</div>}
                  {shop.phone && <div style={{ display: 'flex', gap: '0.4rem', fontSize: '0.78rem', color: 'var(--text-2)' }}><Phone size={12} style={{ flexShrink: 0, marginTop: 2 }}/> {shop.phone}</div>}
                  {shop.commerceType && <div style={{ display: 'flex', gap: '0.4rem', fontSize: '0.78rem', color: 'var(--text-2)' }}><Package size={12} style={{ flexShrink: 0, marginTop: 2 }}/> {shop.commerceType.replace('_', ' ')}</div>}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.2rem' }}>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: STATUS_COLORS[shop.status] || 'var(--text-3)' }}>
                      ● {STATUS_LABELS[shop.status] || 'Fermé'}
                    </span>
                    <PackBadge packId={shop.pack || 'free'}/>
                  </div>
                  {shop.rejectionReason && (
                    <div style={{ display: 'flex', gap: '0.4rem', fontSize: '0.75rem', color: '#f0564a', background: 'rgba(240,86,74,0.08)', padding: '0.4rem 0.6rem', borderRadius: 'var(--r-sm)' }}>
                      <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 2 }}/> {shop.rejectionReason}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  <button onClick={() => setSelected(shop)} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.85rem', borderRadius: 'var(--r-lg)', border: 'none', background: 'rgba(0,201,167,0.12)', color: '#00c9a7', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <Check size={12}/> Décision
                  </button>
                  <button onClick={() => onForceStatus(shop.id, shop.status)} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.85rem', borderRadius: 'var(--r-lg)', border: 'none', background: shop.adminLockedStatus ? 'rgba(99,102,241,0.20)' : 'rgba(99,102,241,0.12)', color: '#818cf8', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit', outline: shop.adminLockedStatus ? '1.5px solid #818cf8' : 'none' }}>
                    {shop.status === 'open' ? <ToggleRight size={12}/> : <ToggleLeft size={12}/>}
                    {shop.status === 'open' ? 'Forcer Fermé' : 'Forcer Ouvert'}
                    {shop.adminLockedStatus && <span style={{ fontSize: '0.6rem', marginLeft: '0.1rem' }}>🔒</span>}
                  </button>
                  {shop.adminLockedStatus && (
                    <button onClick={() => onUnlockStatus(shop.id)} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.85rem', borderRadius: 'var(--r-lg)', border: 'none', background: 'rgba(245,158,11,0.12)', color: '#f59e0b', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <Unlock size={12}/> Déverrouiller
                    </button>
                  )}
                  <button onClick={() => setConfirm({ id: shop.id, ownerId: shop.ownerId, name: shop.name })} style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.85rem', borderRadius: 'var(--r-lg)', border: 'none', background: 'rgba(240,86,74,0.12)', color: '#f0564a', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                    <Trash2 size={12}/> Supprimer
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {selected && (
        <ApprovalModal shop={selected} onClose={() => setSelected(null)} onApprove={onApprove} onReject={onReject}/>
      )}
      {confirm && (
        <ConfirmModal
          message={`Supprimer définitivement "${confirm.name}" ? Cette action est irréversible.`}
          onConfirm={() => { onDelete(confirm.id, confirm.ownerId); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ONGLET 2 — COMMERÇANTS
───────────────────────────────────────────────────────────────── */
const MerchantsTab = ({ shops }) => {
  const [merchants, setMerchants] = useState([]);
  const [users, setUsers]         = useState({});
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [confirm, setConfirm]     = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'merchants'), snap => {
      setMerchants(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'users'), snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = d.data(); });
      setUsers(map);
    });
    return () => unsub();
  }, []);

  const suspend = async (uid, isSuspended) => {
    await updateDoc(doc(db, 'users', uid), { suspended: !isSuspended });
    await updateDoc(doc(db, 'merchants', uid), { suspended: !isSuspended }).catch(() => {});
  };

  const deleteMerchant = async (uid) => {
    const mSnap = await getDoc(doc(db, 'merchants', uid));
    const shopId = mSnap.data()?.shopId;
    if (shopId) await deleteDoc(doc(db, 'shops', shopId));
    await deleteDoc(doc(db, 'merchants', uid));
    await deleteDoc(doc(db, 'users', uid)).catch(() => {});
  };

  const filtered = merchants.filter(m => {
    const u = users[m.id] || {};
    const name = u.name || m.ownerName || '';
    const email = u.email || '';
    return !search || name.toLowerCase().includes(search.toLowerCase()) || email.toLowerCase().includes(search.toLowerCase());
  });

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>Chargement…</div>;

  return (
    <div>
      <div style={{ position: 'relative', margin: '0 1rem 0.75rem' }}>
        <Search size={15} style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}/>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par nom ou email…"
          style={{ width: '100%', padding: '0.65rem 0.85rem 0.65rem 2.4rem', borderRadius: 'var(--r-lg)', border: '1.5px solid var(--outline)', background: 'var(--bg-elevated)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}/>
      </div>

      <div style={{ padding: '0 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {filtered.length === 0 && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)', fontSize: '0.85rem' }}>Aucun commerçant.</div>}
        {filtered.map(m => {
          const u = users[m.id] || {};
          const shop = shops.find(s => s.id === m.shopId);
          const isSuspended = u.suspended || m.suspended;

          return (
            <div key={m.id} className="card" style={{ margin: 0, padding: '0.85rem 1rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: isSuspended ? 'rgba(240,86,74,0.12)' : 'rgba(0,201,167,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <User size={16} color={isSuspended ? '#f0564a' : '#00c9a7'} strokeWidth={1.8}/>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text)' }}>{u.name || m.ownerName || '—'}</div>
                    {isSuspended && <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.1rem 0.4rem', borderRadius: '999px', background: 'rgba(240,86,74,0.15)', color: '#f0564a' }}>SUSPENDU</span>}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: '0.1rem' }}>{u.email || '—'}</div>
                  {shop && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.35rem' }}>
                      <Store size={11} color="var(--primary)"/>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-2)', fontWeight: 600 }}>{shop.name}</span>
                      <PackBadge packId={shop.pack || 'free'}/>
                    </div>
                  )}
                  {!m.hasShop && <div style={{ fontSize: '0.7rem', color: 'var(--text-3)', marginTop: '0.25rem' }}>Pas encore de magasin</div>}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
                <button onClick={() => suspend(m.id, isSuspended)} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.45rem 0.8rem', borderRadius: 'var(--r-lg)', border: 'none', background: isSuspended ? 'rgba(0,201,167,0.12)' : 'rgba(245,166,35,0.12)', color: isSuspended ? '#00c9a7' : '#f5a623', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                  {isSuspended ? <><Unlock size={11}/> Réactiver</> : <><Lock size={11}/> Suspendre</>}
                </button>
                <button onClick={() => setConfirm({ id: m.id, name: u.name || m.ownerName || 'ce commerçant' })} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.45rem 0.8rem', borderRadius: 'var(--r-lg)', border: 'none', background: 'rgba(240,86,74,0.12)', color: '#f0564a', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Trash2 size={11}/> Supprimer
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {confirm && (
        <ConfirmModal
          message={`Supprimer "${confirm.name}" et toutes ses données ? Action irréversible.`}
          onConfirm={() => { deleteMerchant(confirm.id); setConfirm(null); }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ONGLET 3 — PACKS
───────────────────────────────────────────────────────────────── */
const PacksTab = ({ shops }) => {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const now = Date.now();

  const withPack = shops.map(s => {
    const packId   = s.pack || 'free';
    const expiry   = s.packExpiry ? (s.packExpiry?.toDate ? s.packExpiry.toDate() : new Date(s.packExpiry)) : null;
    const isExpired = packId !== 'free' && expiry && expiry.getTime() <= now;
    const daysLeft  = expiry ? Math.ceil((expiry.getTime() - now) / 86400000) : null;
    return { ...s, packId, expiry, isExpired, daysLeft };
  });

  const counts = {
    all:     withPack.length,
    free:    withPack.filter(s => s.packId === 'free').length,
    paid:    withPack.filter(s => s.packId !== 'free').length,
    expired: withPack.filter(s => s.isExpired).length,
    expiring: withPack.filter(s => !s.isExpired && s.daysLeft !== null && s.daysLeft <= 7).length,
  };

  const filtered = withPack
    .filter(s => {
      if (filter === 'paid')    return s.packId !== 'free';
      if (filter === 'free')    return s.packId === 'free';
      if (filter === 'expired') return s.isExpired;
      if (filter === 'expiring') return !s.isExpired && s.daysLeft !== null && s.daysLeft <= 7;
      return true;
    })
    .filter(s => !search || s.name?.toLowerCase().includes(search.toLowerCase()));

  const FILTERS = [
    { key: 'all',      label: 'Tous',         count: counts.all },
    { key: 'paid',     label: 'Payants',       count: counts.paid },
    { key: 'free',     label: 'Gratuits',      count: counts.free },
    { key: 'expiring', label: '⚠️ Expirent bientôt', count: counts.expiring },
    { key: 'expired',  label: '❌ Expirés',    count: counts.expired },
  ];

  const forcePack = async (shopId, packId) => {
    await updateDoc(doc(db, 'shops', shopId), { pack: packId, packExpiry: null, updatedAt: serverTimestamp() });
  };

  return (
    <div>
      {/* Stats packs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', padding: '0 1rem', marginBottom: '0.75rem' }}>
        {Object.entries(PACK_CFG).map(([k, cfg]) => {
          const Icon = cfg.icon;
          const count = withPack.filter(s => s.packId === k).length;
          return (
            <div key={k} style={{ background: `${cfg.color}12`, borderRadius: 'var(--r-lg)', padding: '0.65rem 0.85rem', display: 'flex', alignItems: 'center', gap: '0.6rem', border: `1px solid ${cfg.color}30` }}>
              <Icon size={16} color={cfg.color} strokeWidth={2}/>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: cfg.color, lineHeight: 1 }}>{count}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-3)', fontWeight: 700 }}>{cfg.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ position: 'relative', margin: '0 1rem 0.75rem' }}>
        <Search size={15} style={{ position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }}/>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un magasin…"
          style={{ width: '100%', padding: '0.65rem 0.85rem 0.65rem 2.4rem', borderRadius: 'var(--r-lg)', border: '1.5px solid var(--outline)', background: 'var(--bg-elevated)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}/>
      </div>

      <div style={{ display: 'flex', gap: '0.35rem', padding: '0 1rem', marginBottom: '0.75rem', overflowX: 'auto' }}>
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{ padding: '0.35rem 0.75rem', borderRadius: '999px', border: 'none', background: filter === f.key ? 'var(--primary)' : 'var(--bg-elevated)', color: filter === f.key ? '#fff' : 'var(--text-2)', fontWeight: 700, fontSize: '0.7rem', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            {f.label} <span style={{ opacity: 0.7 }}>({f.count})</span>
          </button>
        ))}
      </div>

      <div style={{ padding: '0 1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {filtered.length === 0 && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)', fontSize: '0.85rem' }}>Aucun magasin.</div>}
        {filtered.map(s => (
          <div key={s.id} className="card" style={{ margin: 0, padding: '0.85rem 1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.65rem' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', flexWrap: 'wrap' }}>
                  <PackBadge packId={s.packId}/>
                  {s.expiry && !s.isExpired && (
                    <span style={{ fontSize: '0.65rem', color: s.daysLeft <= 7 ? '#f59e0b' : 'var(--text-3)', fontWeight: 600 }}>
                      <Calendar size={10} style={{ verticalAlign: 'middle', marginRight: 2 }}/>
                      {s.daysLeft > 0 ? `Expire dans ${s.daysLeft}j` : 'Expire aujourd\'hui'}
                    </span>
                  )}
                  {s.isExpired && <span style={{ fontSize: '0.65rem', color: '#f0564a', fontWeight: 700 }}>Expiré</span>}
                </div>
              </div>
            </div>

            {/* Forcer un pack */}
            <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.65rem', color: 'var(--text-3)', fontWeight: 700, alignSelf: 'center', marginRight: '0.1rem' }}>Forcer :</span>
              {Object.entries(PACK_CFG).map(([k, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button key={k} onClick={() => forcePack(s.id, k)} disabled={s.packId === k} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.3rem 0.6rem', borderRadius: '999px', border: `1px solid ${cfg.color}40`, background: s.packId === k ? `${cfg.color}20` : 'transparent', color: cfg.color, fontWeight: 700, fontSize: '0.65rem', cursor: s.packId === k ? 'default' : 'pointer', fontFamily: 'inherit', opacity: s.packId === k ? 1 : 0.7 }}>
                    <Icon size={9} strokeWidth={2}/> {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ONGLET 4 — NOTIFICATIONS PUSH
───────────────────────────────────────────────────────────────── */
const NotificationsTab = ({ shops }) => {
  const [title, setTitle]     = useState('');
  const [body, setBody]       = useState('');
  const [target, setTarget]   = useState('all'); // 'all' | 'approved' | 'pack_paid'
  const [sending, setSending] = useState(false);
  const [sent, setSent]       = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'admin_notifications'), orderBy('sentAt', 'desc')),
      snap => setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => unsub();
  }, []);

  const TARGET_LABELS = {
    all:       'Tous les commerçants',
    approved:  'Commerçants approuvés',
    pack_paid: 'Commerçants avec pack payant',
  };

  const getTargetShops = () => {
    if (target === 'approved')  return shops.filter(s => s.approval === 'approved');
    if (target === 'pack_paid') return shops.filter(s => s.pack && s.pack !== 'free');
    return shops;
  };

  const sendNotification = async () => {
    if (!title.trim() || !body.trim()) return;
    setSending(true);
    try {
      const targetShops = getTargetShops();
      // Save to Firestore for history + worker can pick up and send FCM
      await import('firebase/firestore').then(async ({ addDoc, collection: col }) => {
        const { addDoc: add } = await import('firebase/firestore');
        await add(col(db, 'admin_notifications'), {
          title: title.trim(),
          body: body.trim(),
          target,
          targetCount: targetShops.length,
          targetShopIds: targetShops.map(s => s.id),
          sentAt: serverTimestamp(),
          status: 'pending', // Worker reads this and sends FCM
        });
      });
      setSent({ count: targetShops.length, title: title.trim() });
      setTitle(''); setBody('');
      setTimeout(() => setSent(null), 5000);
    } finally {
      setSending(false);
    }
  };

  const targetCount = getTargetShops().length;

  return (
    <div style={{ padding: '0 1rem' }}>
      {/* Formulaire envoi */}
      <div className="card" style={{ margin: '0 0 1rem', padding: '1.1rem' }}>
        <div style={{ fontWeight: 800, fontSize: '0.88rem', color: 'var(--text)', marginBottom: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <Bell size={15} color="var(--primary)"/> Envoyer une notification
        </div>

        {/* Cible */}
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-2)', display: 'block', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Destinataires</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {Object.entries(TARGET_LABELS).map(([k, lbl]) => (
              <label key={k} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', padding: '0.5rem 0.75rem', borderRadius: 'var(--r-lg)', background: target === k ? 'rgba(0,201,167,0.08)' : 'var(--bg-elevated)', border: `1.5px solid ${target === k ? 'var(--primary)' : 'transparent'}`, transition: 'all 0.15s' }}>
                <input type="radio" name="target" value={k} checked={target === k} onChange={() => setTarget(k)} style={{ accentColor: 'var(--primary)' }}/>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)' }}>{lbl}</span>
                {target === k && <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--primary)', fontWeight: 700 }}>{targetCount} magasin{targetCount > 1 ? 's' : ''}</span>}
              </label>
            ))}
          </div>
        </div>

        {/* Titre */}
        <div style={{ marginBottom: '0.6rem' }}>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-2)', display: 'block', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Titre *</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex: Nouvelle fonctionnalité disponible !" maxLength={80}
            style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: 'var(--r-lg)', border: '1.5px solid var(--outline)', background: 'var(--bg-elevated)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}/>
        </div>

        {/* Message */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-2)', display: 'block', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Message *</label>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Contenu de la notification…" maxLength={200}
            style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: 'var(--r-lg)', border: '1.5px solid var(--outline)', background: 'var(--bg-elevated)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '0.85rem', resize: 'none', outline: 'none', boxSizing: 'border-box' }}/>
          <div style={{ textAlign: 'right', fontSize: '0.65rem', color: 'var(--text-3)', marginTop: '0.2rem' }}>{body.length}/200</div>
        </div>

        {sent && (
          <div style={{ marginBottom: '0.75rem', padding: '0.65rem 0.85rem', borderRadius: 'var(--r-lg)', background: 'rgba(0,201,167,0.1)', border: '1px solid rgba(0,201,167,0.3)', fontSize: '0.8rem', color: '#00c9a7', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <CheckCircle size={14}/> Notification envoyée à {sent.count} commerçant{sent.count > 1 ? 's' : ''} !
          </div>
        )}

        <button onClick={sendNotification} disabled={sending || !title.trim() || !body.trim()} style={{ width: '100%', padding: '0.75rem', borderRadius: 'var(--r-lg)', border: 'none', background: (sending || !title.trim() || !body.trim()) ? 'var(--outline)' : 'linear-gradient(135deg,var(--primary),#00b396)', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: (sending || !title.trim() || !body.trim()) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.45rem', transition: 'all 0.15s' }}>
          {sending ? <><RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }}/> Envoi…</> : <><Send size={14}/> Envoyer à {targetCount} commerçant{targetCount > 1 ? 's' : ''}</>}
        </button>
      </div>

      {/* Historique */}
      {history.length > 0 && (
        <div>
          <div style={{ fontWeight: 800, fontSize: '0.8rem', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '0.5rem' }}>Historique</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {history.slice(0, 10).map(n => (
              <div key={n.id} className="card" style={{ margin: 0, padding: '0.7rem 0.9rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--text)' }}>{n.title}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-2)', marginTop: '0.2rem' }}>{n.body}</div>
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-3)' }}>{TARGET_LABELS[n.target]} · {n.targetCount} dest.</span>
                  <span style={{ fontSize: '0.65rem', color: 'var(--text-3)' }}>{n.sentAt?.toDate ? n.sentAt.toDate().toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   COMPOSANT PRINCIPAL — AdminDashboard
───────────────────────────────────────────────────────────────── */
const AdminDashboard = () => {
  const { isAdmin }           = useAuth();
  const navigate              = useNavigate();
  const [shops, setShops]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('shops');
  const { total, today: todayVisits } = useVisitorStats();

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
  }, [isAdmin, navigate]);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'shops'), orderBy('createdAt', 'desc')),
      snap => { setShops(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); }
    );
    return () => unsub();
  }, []);

  const approveShop = useCallback(async (shopId) => {
    await updateDoc(doc(db, 'shops', shopId), { approval: 'approved', rejectionReason: '', approvedAt: serverTimestamp() });
  }, []);

  const rejectShop = useCallback(async (shopId, reason) => {
    await updateDoc(doc(db, 'shops', shopId), { approval: 'rejected', rejectionReason: reason });
  }, []);

  const deleteShop = useCallback(async (shopId, ownerId) => {
    await deleteDoc(doc(db, 'shops', shopId));
    if (ownerId) {
      try { await updateDoc(doc(db, 'merchants', ownerId), { shopId: null, hasShop: false }); } catch {}
    }
  }, []);

  const forceStatus = useCallback(async (shopId, currentStatus) => {
    const newStatus = (currentStatus || 'closed') === 'open' ? 'closed' : 'open';
    /* adminLockedStatus = true empêche le commerçant de changer le statut */
    await updateDoc(doc(db, 'shops', shopId), {
      status: newStatus,
      adminLockedStatus: true,
      updatedAt: serverTimestamp(),
    });
  }, []);

  const unlockStatus = useCallback(async (shopId) => {
    await updateDoc(doc(db, 'shops', shopId), {
      adminLockedStatus: false,
      updatedAt: serverTimestamp(),
    });
  }, []);

  const counts = {
    pending:  shops.filter(s => !s.approval || s.approval === 'pending').length,
    approved: shops.filter(s => s.approval === 'approved').length,
  };

  const TABS = [
    { key: 'shops',        label: 'Magasins',     icon: Store,    badge: counts.pending },
    { key: 'merchants',    label: 'Commerçants',  icon: Users,    badge: null },
    { key: 'packs',        label: 'Packs',        icon: Crown,    badge: null },
    { key: 'notifications',label: 'Notifs',       icon: Bell,     badge: null },
  ];

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-2)' }}>Chargement…</div>;

  return (
    <div className="page-enter" style={{ paddingBottom: '2rem' }}>

      {/* HERO */}
      <div className="admin-hero">
        <div className="admin-hero__blob"/>
        <div className="admin-hero__top">
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <div className="admin-hero__icon"><Shield size={18} strokeWidth={2}/></div>
            <div>
              <p className="admin-hero__label">Administration</p>
              <h2 className="admin-hero__title">Dashboard Admin</h2>
            </div>
          </div>
        </div>
        <div className="admin-hero__stats">
          <div className="admin-hero__stat">
            <Eye size={14} style={{ opacity: .7 }}/>
            <div>
              <div className="admin-hero__stat-num">{typeof total === 'number' ? total.toLocaleString() : total}</div>
              <div className="admin-hero__stat-lbl">Visites totales</div>
            </div>
          </div>
          <div className="admin-hero__stat-div"/>
          <div className="admin-hero__stat">
            <TrendingUp size={14} style={{ opacity: .7 }}/>
            <div>
              <div className="admin-hero__stat-num">{todayVisits}</div>
              <div className="admin-hero__stat-lbl">Aujourd'hui</div>
            </div>
          </div>
          <div className="admin-hero__stat-div"/>
          <div className="admin-hero__stat">
            <ShoppingBag size={14} style={{ opacity: .7 }}/>
            <div>
              <div className="admin-hero__stat-num">{shops.length}</div>
              <div className="admin-hero__stat-lbl">Magasins</div>
            </div>
          </div>
        </div>
      </div>

      {/* STAT CARDS */}
      <div className="admin-stats-grid">
        <StatCard icon={Clock}       iconBg="rgba(245,166,35,.12)" iconColor="#f5a623" label="En attente"   value={counts.pending}  sub="À traiter"/>
        <StatCard icon={CheckCircle} iconBg="rgba(0,201,167,.12)"  iconColor="#00c9a7" label="Approuvés"    value={counts.approved} sub="En ligne"/>
        <StatCard icon={Crown}       iconBg="rgba(245,158,11,.12)" iconColor="#f59e0b" label="Packs payants" value={shops.filter(s => s.pack && s.pack !== 'free').length} sub="Abonnés"/>
        <StatCard icon={BarChart2}   iconBg="rgba(45,63,143,.12)"  iconColor="#818cf8" label="Visites/jour"  value={todayVisits}     sub="Aujourd'hui"/>
      </div>

      {/* ONGLETS PRINCIPAUX */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--outline)', margin: '0 1rem 0', overflowX: 'auto' }}>
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ flex: 1, minWidth: 60, padding: '0.7rem 0.5rem', background: 'none', border: 'none', borderBottom: `2.5px solid ${activeTab === t.key ? 'var(--primary)' : 'transparent'}`, color: activeTab === t.key ? 'var(--primary)' : 'var(--text-2)', fontWeight: 700, fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.2rem', transition: 'color 0.15s', position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                <Icon size={16} strokeWidth={2}/>
                {t.badge > 0 && (
                  <span style={{ position: 'absolute', top: -5, right: -7, background: '#f0564a', color: '#fff', borderRadius: '50%', width: 14, height: 14, fontSize: '0.55rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{t.badge}</span>
                )}
              </div>
              {t.label}
            </button>
          );
        })}
      </div>

      {/* CONTENU PAR ONGLET */}
      <div style={{ marginTop: '0.75rem' }}>
        {activeTab === 'shops'         && <ShopsTab shops={shops} onApprove={approveShop} onReject={rejectShop} onDelete={deleteShop} onForceStatus={forceStatus} onUnlockStatus={unlockStatus}/>}
        {activeTab === 'merchants'     && <MerchantsTab shops={shops}/>}
        {activeTab === 'packs'         && <PacksTab shops={shops}/>}
        {activeTab === 'notifications' && <NotificationsTab shops={shops}/>}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default AdminDashboard;